import { saveNodeById } from '../../api/node';
import { CircularProgress, FormControlLabel, Grid, makeStyles, FormGroup, Typography, Checkbox } from '@material-ui/core';
import classNames from 'classnames';
import RouteLeavingGuard from '../PageLeaveConfirmationDialog/RouteLeavingGuard';
import { EditorialType, Node, NodeStatus, NodeTypeName, User, UserRole } from '../../domain/domain';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ApplicationState } from '../../reducers';
import { UiActionType } from '../../reducers/ui.reducer';
import EditorBottomBar from './EditorBottomBar';
import { isShowRevisions, removePTags } from './utils';
import DMP from './DMP';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import ApproveComponentDialog from './ApproveComponentDialog';
import RejectComponentDialog from './RejectComponentDialog';
import TextEditorDialog from './TextEditorDialog';
import TextEditor from '../TextEditor/TextEditor';
import isEqual from 'lodash/isEqual';

const useStyles = makeStyles(theme => ({
    goldenRecordPanel: {
        border: '1px solid #ddd',
        padding: 10,
        height: '100%',
    },
    workingCopyPanel: {
        border: '1px solid #ddd',
        padding: 10,
        height: '100%',
    },
    diffCSS: {
        borderTop: '1px solid #ddd',
        fontSize: '12px',
        paddingTop: 10,
        minHeight: 300,
    },
    header: {
        color: '#a9aeb1',
        paddingLeft: 8,
        fontSize: 16
    },
    editorArea: {
        marginTop: 0,
    },
    root: {
    },
    topPanel: {
        height: '85%'
    },
    maxHeight: {
        height: '100%'
    },
    part: {
        maxHeight: '100%'
    },
    sections: {
        fontWeight: 'bold'
    },
    approvedDate: {
        fontWeight: 'normal'
    }
}));

type Props = {
    className?: string,
    onRejectNode: (node: Node) => void,
    onApproveNode: (node: Node) => void,
    parseHtmlForEditor: (html: string) => string,
    replaceStrongWithBold: (content: string) => string,
    sanitizeContent: (content: string) => string,
}

const GoldenRecordEditorV2: React.FC<Props> = ({ className, onRejectNode, onApproveNode, parseHtmlForEditor, replaceStrongWithBold, sanitizeContent }) => {

    const mode = useSelector((app: ApplicationState) => app.comp.editorMode);
    const editorNode: Node | undefined = useSelector((app: ApplicationState) => app.comp.editorNode);
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const [checked, setChecked] = useState<boolean>(false)
    const markedForDeletion = useSelector((app: ApplicationState) => app.comp.statuteMarkForDeleteCheckBox);
    const [contentChanged, setContentChanged] = useState<boolean>(false)
    const [hasActiveChange, setHasActiveChange] = useState(false);

    let currentUser: User | undefined = useSelector((app: ApplicationState) => app.user.currentUser);

    const dispatch = useDispatch();

    const [workingCopyEditorState, setWorkingCopyEditorState] = useState<string>("");

    const onCancel = () => {
        if (editorNode) {
            setWorkingCopyEditorState(editorNode.nodeHistory ? editorNode.nodeHistory.content : editorNode.content);
        }
    }

    const handleRevisions = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    };

    const handleDelete = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ComponentManagementActionType.STATUTE_MARK_FOR_DELETION, mark: event.target.checked });
        markForDeletion()
    };

    const markForDeletion = () => {
        if (editorNode) {
            let newEditorNode = { ...editorNode }

            if (editorNode) {
                newEditorNode.markedForDeletion = !markedForDeletion;
            }
            saveNodeById(newEditorNode)
            dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: newEditorNode });
        }
    }

    const saveWorkingCopyContent = () => {
        if (!editorNode) {
            return;
        }
        let newContent = workingCopyEditorState;

        if (!editorNode.nodeHistory) {
            editorNode.nodeHistory = {
                content: newContent,
                heading: editorNode.heading,
                citation: editorNode.citation,
            }
        } else {
            editorNode.nodeHistory.content = newContent
        }
        editorNode.status = NodeStatus.CHANGED_NOT_APPROVED;

        saveNodeById(editorNode).then(response => {
            if (!editorNode) {
                return;
            }
            const hasChanges = response.data.nodeHistory?.content !== response.data.content;

            dispatch({ type: ComponentManagementActionType.SUBTREE_CHANGED, nodeId: response.data.nodeId, hasChanges: hasChanges });
            dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
            dispatch({
                type: ComponentManagementActionType.STATUTORY_CONTENT_CHANGED,
                nodeId: response.data.nodeId,
                statutoryContentHasChanges: hasChanges,
            })
            dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Working Copy saved.", severity: "success" } });
        }).catch(error => {
            console.error(error);
        });
    }

    const displayHeadings = (editorNode: Node) => {
        if (editorNode.heading === undefined || editorNode.heading.length === 0) {
            return editorNode.citation;
        } else if (editorNode.citation === undefined) {
            return editorNode.heading;
        }
        else {
            return editorNode.citation;
        }
    }

    const displayApprovedDate = (editorNode: Node) => {
        if (editorNode.lastApprovedDate) {
            let approvedDate = new Date(editorNode.lastApprovedDate);
            return "Last Approved on " + approvedDate.toLocaleDateString();
        } else {
            return "";
        }
    }

    const handleContentChange = (val: boolean) => setContentChanged(val);

    useEffect(() => {
        dispatch({ type: ComponentManagementActionType.STATUTE_MARK_FOR_DELETION, mark: false });
        if (!editorNode) {
            return;
        }
        if (editorNode.markedForDeletion) {
            dispatch({ type: ComponentManagementActionType.STATUTE_MARK_FOR_DELETION, mark: true });
        }
        setWorkingCopyEditorState(editorNode.nodeHistory ? editorNode.nodeHistory.content : editorNode.content);
    }, [editorNode]);

    useEffect(() => {
        onCancel();
        dispatch({ type: ComponentManagementActionType.EDITOR_MODE_UPDATED, editorMode: "view" });
    }, [location.pathname]);

    const isMarkForDeletionDisabled = () => {
        return currentUser && [UserRole.NON_PUBLICATIONS_TEAM].includes(currentUser?.userRole)
    }

    let initialContent = parseHtmlForEditor(workingCopyEditorState)?.replace(/\u200B/g, '');
    return (
        <>
            <RouteLeavingGuard
                when={mode === "edit"}
                text={!hasActiveChange ? "Are you sure you want to exit edit mode?" : "You have made a change. Are you sure you want to exit?  All changes will be lost"}
                navigate={(path: string) => history.push(path)}
                shouldBlockNavigation={(location: string) => {
                    return true;
                }}
            />
            <ApproveComponentDialog onApprove={onApproveNode} />
            <RejectComponentDialog onReject={onRejectNode} />
            <Grid container direction='column' className={classNames(classes.root, "flex-gap-1", className)} wrap='nowrap'>
                <Grid item className={classes.topPanel}>
                    <Grid direction='row' container className={`${classes.maxHeight} flex-gap-1`} wrap='nowrap'>
                        <Grid item xs={6} className={`${classes.part} last-approved-version-window`}>
                            <Grid container direction='column' className={classes.goldenRecordPanel} wrap='nowrap'>
                                <Grid item>
                                    <Typography variant='h4' className={classes.header}>LAST APPROVED VERSION</Typography>
                                </Grid>
                                {editorNode &&
                                    <Grid item style={{ paddingLeft: '8px' }}>
                                        <Typography variant='h4' className={classes.sections}>{displayHeadings(editorNode)}</Typography>
                                        <Typography variant='h5' className={classes.approvedDate}>{displayApprovedDate(editorNode)}</Typography>
                                    </Grid>
                                }
                                <Grid item style={{ flexGrow: 1 }} className={classes.editorArea}>
                                    {!editorNode &&
                                        <CircularProgress />
                                    }
                                    {editorNode &&
                                        <TextEditor
                                            showToolbar={false}
                                            showEditorialTemplates={true}
                                            selectedEditorialType={EditorialType.INLINE_EDITOR}
                                            readOnly={true}
                                            editorStyle={{ maxHeight: 500 }}
                                            html={parseHtmlForEditor(editorNode.content)}
                                            onChange={(newValue) => { }}
                                        />
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={6} className={classes.part}>
                            <Grid container direction='column' className={classes.workingCopyPanel} wrap="nowrap">
                                <Grid item>
                                    <Grid container direction='row' justifyContent="space-between" alignItems="flex-start" wrap="nowrap">
                                        <Grid item>
                                            <Typography variant='h4' className={classes.header}>WORKING COPY</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item className={classes.editorArea}>
                                    {!editorNode &&
                                        <CircularProgress />
                                    }
                                    {editorNode && mode === "edit" ? (
                                        <TextEditorDialog
                                            title={"Editing Statutes"}
                                            citation={editorNode.citation}
                                            open={true}
                                            onNodeClicked={() => console.log()}
                                            Actions={
                                                <EditorBottomBar
                                                    hasActiveChange={hasActiveChange}
                                                    onCancelClicked={() => {
                                                        setHasActiveChange(false);
                                                        setContentChanged(false);
                                                        dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: false });
                                                        onCancel();
                                                    }}
                                                    onSaveClicked={() => {
                                                        setHasActiveChange(false);
                                                        setContentChanged(false);
                                                        saveWorkingCopyContent();
                                                    }
                                                    }
                                                    changer={() => {
                                                        dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: true });
                                                    }}
                                                    contentChanged={contentChanged}
                                                />
                                            }
                                        >
                                            <Grid container direction='column' className='editor-dialog'>
                                                <Grid item>
                                                    <TextEditor
                                                        showToolbar={mode === "edit" && editorNode.nodeType !== NodeTypeName.TITLE}
                                                        selectedEditorialType={EditorialType.INLINE_EDITOR}
                                                        html={parseHtmlForEditor(workingCopyEditorState)}
                                                        editorStyle={{}}
                                                        showEditorialTemplates={true}
                                                        onChange={(newContent: string) => {
                                                            newContent = editorNode.nodeType === NodeTypeName.TITLE ? removePTags(newContent) : newContent;
                                                            const cleanedContent = newContent.trim();
                                                            const content = (['<p><br></p>', '<p><br/></p>'].includes(cleanedContent))
                                                                ? ''
                                                                : newContent;
                                                            const parsedContent = sanitizeContent(content);
                                                            const parsedContentWithBold = replaceStrongWithBold(parsedContent);
                                                            setHasActiveChange(true);
                                                            handleContentChange(!isEqual(initialContent, newContent));
                                                            dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: true });
                                                            setWorkingCopyEditorState(parsedContentWithBold);
                                                        }}
                                                        readOnly={false}
                                                    />
                                                </Grid>
                                                <Grid item className={classes.diffCSS} style={{ height: '50%' }}>
                                                    <h1 style={{ fontWeight: 'bold', textDecoration: 'underline', paddingBottom: '10px' }}>REVISIONS:</h1>
                                                    <DMP string1={editorNode?.content} string2={workingCopyEditorState} />
                                                </Grid>
                                            </Grid>
                                        </TextEditorDialog>
                                    ) : (
                                        <>
                                            <FormGroup style={{ flexDirection: 'row', paddingLeft: 0 }}>
                                                <FormControlLabel control={<Checkbox
                                                    checked={checked}
                                                    onChange={handleRevisions}
                                                    inputProps={{ 'aria-label': 'controlled' }}
                                                />} label="Show Revisions" />
                                                {editorNode?.nodeType !== NodeTypeName.TITLE && (
                                                    <FormControlLabel control={<Checkbox
                                                        checked={markedForDeletion}
                                                        onChange={handleDelete}
                                                        disabled={isMarkForDeletionDisabled()}
                                                        inputProps={{ 'aria-label': 'controlled' }}
                                                    />} label="Mark For Deletion" />)}
                                            </FormGroup>
                                            <Grid item>
                                                {mode === "view" &&
                                                    <EditorBottomBar
                                                        hasActiveChange={hasActiveChange}
                                                        onCancelClicked={() => {
                                                            dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: true });
                                                            onCancel();
                                                        }}
                                                        onSaveClicked={() => {
                                                            saveWorkingCopyContent();
                                                        }}
                                                        changer={() => {
                                                            dispatch({ type: ComponentManagementActionType.DIFF_HAS_CHANGES, diffChanges: true });
                                                        }}
                                                    />
                                                }
                                            </Grid>
                                            <TextEditor
                                                showToolbar={mode === "edit"}
                                                html={workingCopyEditorState && parseHtmlForEditor(workingCopyEditorState)}
                                                editorStyle={{ maxHeight: 400 }}
                                                selectedEditorialType={EditorialType.INLINE_EDITOR}
                                                onChange={(_: string) => { }}
                                                readOnly={true}
                                            />
                                            {isShowRevisions(editorNode?.content!, workingCopyEditorState) && checked &&
                                                <div className={classes.diffCSS}>
                                                    <h1 style={{ fontWeight: 'bold', textDecoration: 'underline', paddingBottom: '10px' }}>REVISIONS:</h1>
                                                    <DMP string1={editorNode?.content} string2={workingCopyEditorState} />
                                                </div>
                                            }
                                            {!isShowRevisions(editorNode?.content!, workingCopyEditorState) && checked && !editorNode?.repealed &&
                                                <div>
                                                    <h1 style={{ fontWeight: 'bold', textDecoration: 'underline', paddingBottom: '10px', fontSize: "28px", color: 'red' }}>No Revisions</h1>
                                                </div>
                                            }
                                            {editorNode?.repealed && checked &&
                                                <div>
                                                    <h1 style={{ fontWeight: 'bold', textDecoration: 'underline', paddingBottom: '10px', fontSize: "28px", color: 'red' }}>Provision Marked for Repeal.</h1>
                                                </div>
                                            }
                                        </>
                                    )
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )

}

export default GoldenRecordEditorV2;
